/**
 * KennelWizardV2 — premium 3-column wizard z live preview (Master Spec v4)
 *
 * Layout:
 *  - Header: hamburger + APISAI logo + tytuł + progress + Pomoc/Zapisz/Dalej
 *  - Left sidebar: kroki z greenchecks + karta „Wskazówka"
 *  - Center: numbered sub-questions per step
 *  - Right: rich live preview (desktop/mobile toggle)
 *
 * Backend: reuse POST/PUT /api/kennels, POST /api/ai/refine, POST /api/ai/generate
 */
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Eye, X, ChevronLeft, ChevronRight, Check, Save, ArrowRight, Globe } from 'lucide-react';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { firePawConfetti } from '@/utils/confetti';
import { haptics } from '@/utils/haptics';

import StudioShell from '@/components/studio/StudioShell';
import { Progress } from '@/components/ui/progress';
import WizardSidebar from './WizardSidebar';
import LivePreview from './LivePreview';
import KennelPublishFlow from './KennelPublishFlow';
import { WizardTopbar } from '@/components/wizard/WizardTopbar';
import { WizardMobileBar } from '@/components/wizard/WizardMobileBar';

import Step1Basics from './steps/Step1Basics';
import Step2About from './steps/Step2About';
import Step3Dogs from './steps/Step3Dogs';
import Step4Litters from './steps/Step4Litters';
import Step5Gallery from './steps/Step5Gallery';
import Step6Contact from './steps/Step6Contact';
import Step7Done from './steps/Step7Done';

// ── Struktura kroków ──────────────────────────────────────────────
// Obowiązuje MASTER v1.0: Kreator Hodowli = 5 kroków (TYLKO strona hodowli).
// Suki / Reproduktory / Mioty to OSOBNE moduły (NIE część kreatora).
//
// Flaga INWIZARD_DOGS_LITTERS = false → wersja docelowa (5 kroków).
// Ustawienie na `true` przywraca pełną wersję 7-krokową (psy + mioty w kreatorze)
// — kod kroków Step3Dogs/Step4Litters POZOSTAJE, nic nie kasujemy (w pełni odwracalne).
const INWIZARD_DOGS_LITTERS = false;

const STEPS_PAGE_ONLY = [
  { id: 1, name: 'Podstawowe informacje', subtitle: 'Nazwa, rasy, lokalizacja', Component: Step1Basics },
  { id: 2, name: 'O hodowli', subtitle: 'Opowiedz o sobie', Component: Step2About },
  { id: 3, name: 'Zdjęcia', subtitle: 'Zdjęcie główne i galeria', Component: Step5Gallery },
  { id: 4, name: 'Kontakt', subtitle: 'Dane kontaktowe i CTA', Component: Step6Contact },
  { id: 5, name: 'Podsumowanie', subtitle: 'Opublikuj i zobacz efekty', Component: Step7Done },
];

const STEPS_FULL = [
  { id: 1, name: 'Podstawy hodowli', subtitle: 'Nazwa, rasy, lokalizacja', Component: Step1Basics },
  { id: 2, name: 'O Twojej hodowli', subtitle: 'Opowiedz o sobie', Component: Step2About },
  { id: 3, name: 'Twoje psy', subtitle: 'Reproduktory i suki', Component: Step3Dogs },
  { id: 4, name: 'Szczenięta / Mioty', subtitle: 'Aktualne i planowane', Component: Step4Litters },
  { id: 5, name: 'Galeria hodowli', subtitle: 'Zdjęcia hodowli i codzienności', Component: Step5Gallery },
  { id: 6, name: 'Kontakt', subtitle: 'Dane kontaktowe i CTA', Component: Step6Contact },
  { id: 7, name: 'Gotowe!', subtitle: 'Opublikuj i zobacz efekty', Component: Step7Done },
];

const STEPS = INWIZARD_DOGS_LITTERS ? STEPS_FULL : STEPS_PAGE_ONLY;

// Deep-link: ?krok=<klucz> → otwórz konkretny krok kreatora (np. z „Edytuj kontakt" w Ustawieniach).
// Mapujemy przez referencję komponentu, więc działa w obu trybach (5-krokowym i 7-krokowym).
const KROK_TO_COMPONENT = {
  podstawy: Step1Basics,
  podstawowe: Step1Basics,
  'o-hodowli': Step2About,
  ohodowli: Step2About,
  opis: Step2About,
  psy: Step3Dogs,
  mioty: Step4Litters,
  zdjecia: Step5Gallery,
  galeria: Step5Gallery,
  kontakt: Step6Contact,
  podsumowanie: Step7Done,
};

const DEFAULT_FORM = {
  name: '',
  species: ['dog'],
  breeds: [],
  postal_code: '',
  city: '',
  voivodeship: '',
  organization_type: '',
  organization: '',
  organization_custom: '',
  is_fci: false,
  description: '',
  cover_image: null,
  logo: null,
  gallery: [],
  dogs: [],
  litters: [],
  email: '',
  phone: '',
  whatsapp: '',
  messenger: '',
  facebook: '',
  instagram: '',
  website: '',
  show_in_catalog: true,
  font_style: 'elegant',
  theme: 'dark',
};

function validateStep(stepId, data) {
  const errors = {};
  if (stepId === 1) {
    if (!data.name?.trim() || data.name.trim().length < 3) errors.name = 'Podaj nazwę hodowli (min. 3 znaki)';
    if (!data.breeds || data.breeds.length === 0) errors.breeds = 'Wybierz co najmniej jedną rasę';
    if (!data.postal_code || !/^\d{2}-\d{3}$/.test(data.postal_code)) errors.postal_code = 'Format XX-XXX';
    if (!data.city?.trim()) errors.city = 'Podaj miasto';
    if (!data.voivodeship) errors.voivodeship = 'Wybierz województwo';
    if (!data.organization_type) errors.organization_type = 'Wybierz związek / stowarzyszenie (możesz wskazać „Brak / nie podano")';
    if (data.organization_type === 'other' && !data.organization_custom?.trim()) errors.organization_custom = 'Podaj nazwę związku / stowarzyszenia';
  }
  return errors;
}

const DRAFT_KEY = 'apisai_kennel_wizard_v2_draft';

export default function KennelWizardV2() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const { token, user } = useAuth();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  const [publishPhase, setPublishPhase] = useState(null); // null | 'warn' | 'success'
  const [publishedSlug, setPublishedSlug] = useState('');
  // drawer state removed — StudioShell handles its own mobile drawer

  // Load existing kennel
  useEffect(() => {
    if (!isEdit) {
      // Load draft for new flow
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft?.form) {
            const mergedForm = { ...DEFAULT_FORM, ...draft.form };
            setForm(mergedForm);
            setCompletedSteps(draft.completedSteps || []);
            // NIE przywracaj użytkownika wprost na krok „Podsumowanie" z pustymi danymi.
            // Jeśli wymagane pola kroku 1 nie są wypełnione → zacznij od kroku 1.
            // W innym wypadku przywróć zapisany krok, ale nigdy ostatni (Podsumowanie).
            let restoredStep = draft.step || 1;
            const step1Invalid = Object.keys(validateStep(1, mergedForm)).length > 0;
            if (step1Invalid) restoredStep = 1;
            else if (restoredStep >= STEPS.length) restoredStep = STEPS.length - 1;
            setStep(restoredStep);
          }
        }
      } catch {}
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/kennels/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const k = res.data;
        setForm({
          ...DEFAULT_FORM,
          name: k.name || '',
          species: k.species || ['dog'],
          breeds: k.breeds || [],
          postal_code: k.postal_code || '',
          city: k.city || '',
          voivodeship: k.voivodeship || '',
          organization_type: k.organization_type || '',
          organization: k.organization || '',
          organization_custom: k.organization_custom || '',
          is_fci: k.is_fci || false,
          description: k.description || '',
          cover_image: k.cover_image || null,
          logo: k.logo || null,
          gallery: k.gallery || [],
          dogs: k.dogs_data || [],
          litters: k.litters_data || [],
          email: k.email || '',
          phone: k.phone || '',
          whatsapp: k.whatsapp || '',
          messenger: k.messenger || '',
          facebook: k.facebook || '',
          instagram: k.instagram || '',
          website: k.website || '',
          show_in_catalog: k.show_in_catalog !== false,
          font_style: k.font_style || 'elegant',
          theme: k.theme || 'dark',
        });
        // Oznacz wszystkie kroki poza ostatnim jako ukończone (dynamicznie wg liczby kroków).
        setCompletedSteps(STEPS.slice(0, -1).map((s) => s.id));
      } catch {
        toast.error('Nie udało się wczytać hodowli');
      } finally {
        setLoadingExisting(false);
      }
    })();
  }, [isEdit, id, token]);

  // Deep-link kroku: ?krok=kontakt → przeskocz od razu do właściwego kroku kreatora.
  useEffect(() => {
    const krok = (searchParams.get('krok') || '').toLowerCase();
    if (!krok) return;
    const target = KROK_TO_COMPONENT[krok];
    if (!target) return;
    const found = STEPS.find((s) => s.Component === target);
    if (found) setStep(found.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Autosave draft
  useEffect(() => {
    if (isEdit) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step, completedSteps }));
    } catch {}
  }, [form, step, completedSteps, isEdit]);

  // SWIPE-BACK gesture (mobile only): swipe from left edge (≤30px) to the right by ≥100px → previous step
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth >= 1024) return;
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let tracking = false;

    const onStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      if (t.clientX > 30) return; // only edge-swipes
      startX = t.clientX;
      startY = t.clientY;
      startTime = Date.now();
      tracking = true;
    };
    const onEnd = (e) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      const dt = Date.now() - startTime;
      // Horizontal swipe right, fast enough, not vertical scroll
      if (dx > 100 && dy < 60 && dt < 600 && step > 1 && !submitting) {
        haptics.tap();
        setStep((s) => Math.max(s - 1, 1));
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, [step, submitting]);

  const updateData = (patch) => {
    setForm((p) => ({ ...p, ...patch }));
    setErrors({});
  };

  const current = STEPS.find((s) => s.id === step);
  const StepComponent = current?.Component;
  const progressPct = useMemo(() => Math.round((step / STEPS.length) * 100), [step]);
  const isLastStep = step === STEPS.length;

  const goToStep = (newStep) => {
    // Wolna nawigacja — user może klikać dowolny krok (backward + forward).
    // Walidacja odbywa się dopiero w handleNext / handlePublish.
    setStep(newStep);
  };

  const handleNext = () => {
    if (isLastStep) {
      attemptPublish();
      return;
    }
    const stepErrors = validateStep(step, form);
    if (Object.keys(stepErrors).length > 0) {
      haptics.error();
      setErrors(stepErrors);
      toast.error('Uzupełnij wymagane pola');
      return;
    }
    haptics.tap();
    setErrors({});
    setCompletedSteps((prev) => Array.from(new Set([...prev, step])));
    setStep(step + 1);
  };

  const handleSaveAndExit = async () => {
    if (!form.name?.trim() || form.breeds?.length === 0) {
      // Save draft locally and exit
      toast.success('Szkic zapisany. Możesz wrócić w każdej chwili.');
      setTimeout(() => navigate('/panel'), 600);
      return;
    }
    // Save to backend as draft
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = { ...form, status: 'draft' };
      if (isEdit) {
        await axios.put(`${API}/kennels/${id}`, payload, { headers });
      } else {
        await axios.post(`${API}/kennels`, payload, { headers });
      }
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      toast.success('Zapisano. Możesz wrócić w każdej chwili.');
      setTimeout(() => navigate('/panel'), 600);
    } catch (e) {
      // Fallback: keep draft locally
      toast.warning('Nie udało się zapisać na serwerze — szkic został zachowany lokalnie.');
      setTimeout(() => navigate('/panel'), 800);
    } finally {
      setSubmitting(false);
    }
  };

  // Walidacja + decyzja: jeśli hodowla nie ma żadnego psa ani miotu — pokazujemy
  // ostrzeżenie o niekompletnym profilu (decyzja Ani). Inaczej publikujemy od razu.
  const attemptPublish = () => {
    const stepErrors = validateStep(1, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      toast.error('Uzupełnij wymagane pola w kroku 1');
      setStep(1);
      return;
    }
    if (!termsAccepted && !isEdit) {
      toast.error('Zaakceptuj regulamin');
      return;
    }
    const noDogs = (form.dogs?.length || 0) === 0;
    const noLitters = (form.litters?.length || 0) === 0;
    if (noDogs && noLitters) {
      setPublishPhase('warn');
      return;
    }
    doPublish();
  };

  // Faktyczna publikacja. `goToPath` — opcjonalna ścieżka po publikacji (np. dodaj sukę).
  const doPublish = async (goToPath = null) => {
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Backend wymaga stringów dla cover_image/logo (nie null) — normalizujemy,
      // aby publikacja hodowli bez wgranych zdjęć nie kończyła się błędem 422.
      const payload = {
        ...form,
        cover_image: form.cover_image || '',
        logo: form.logo || '',
        dogs_data: form.dogs,
        litters_data: form.litters,
      };
      let result;
      if (isEdit) {
        const res = await axios.put(`${API}/kennels/${id}`, payload, { headers });
        result = res.data;
      } else {
        const res = await axios.post(`${API}/kennels`, payload, { headers });
        result = res.data;
      }
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      setPublishedSlug(result?.slug || form?.slug || '');
      firePawConfetti();
      toast.success(isEdit ? 'Hodowla zaktualizowana' : 'Hodowla opublikowana!');
      if (goToPath) {
        setPublishPhase(null);
        navigate(goToPath);
      } else {
        setPublishPhase('success');
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Nie udało się zapisać';
      toast.error(typeof detail === 'string' ? detail : 'Błąd zapisu');
      setPublishPhase(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingExisting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // Topbar slot dla wizarda — wspólny WizardTopbar (jedno źródło prawdy dla wszystkich kreatorów)
  const wizardTopbarSlot = (
    <WizardTopbar
      prefix="wizard-v2"
      step={step}
      totalSteps={STEPS.length}
      stepName={current?.name}
      progressPct={progressPct}
      isLastStep={isLastStep}
      submitting={submitting}
      nextDisabled={submitting || (isLastStep && !termsAccepted && !isEdit)}
      publishLabel="Opublikuj"
      onSaveExit={handleSaveAndExit}
      onNext={handleNext}
      viewHref={form?.slug ? `/hodowla/${form.slug}` : undefined}
    />
  );

  return (
    <StudioShell
      mode="workspace"
      contextLabel="Strona hodowli"
      topbarSlot={wizardTopbarSlot}
      activeKey="hodowla"
      sidebarMuted={true}
      backTo="/panel"
      backLabel="Wróć do panelu"
    >
      <div data-testid="kennel-wizard-v2">
      <KennelPublishFlow
        phase={publishPhase}
        submitting={submitting}
        slug={publishedSlug}
        userName={user?.name}
        entityName={form.name}
        coverImage={form.cover_image || (form.gallery && form.gallery[0])}
        subtitle={form.breeds?.[0]}
        hasDogs={(form.dogs?.length || 0) > 0}
        onPublishAnyway={() => doPublish()}
        onAddAndPublish={(path) => doPublish(path)}
        onNavigate={(path) => { setPublishPhase(null); navigate(path); }}
        onClose={() => setPublishPhase(null)}
      />

      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 pb-24 lg:pb-8">
        <div className="flex flex-col lg:flex-row gap-6 xl:gap-8 items-start">
          {/* LEFT WIZARD STEP SIDEBAR — ukryty na kroku Podsumowanie (pełna szerokość) */}
          {!isLastStep && (
          <WizardSidebar
            steps={STEPS}
            currentStep={step}
            completedSteps={completedSteps}
            onStepClick={goToStep}
            title={<>Budujemy prezentację <span className="text-orange-500">hodowli</span></>}
          />
          )}

          {/* CENTER FORM (na kroku Podsumowanie — treść od lewej, bez ramki karty; podgląd zostaje po prawej) */}
          <section className={`flex-1 min-w-0 w-full ${isLastStep ? '' : 'apisai-soft-card p-5 sm:p-7'}`} data-testid="wizard-v2-form-panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.18 }}
              >
                {StepComponent && (
                  <StepComponent
                    data={form}
                    updateData={updateData}
                    errors={errors}
                    termsAccepted={termsAccepted}
                    setTermsAccepted={setTermsAccepted}
                    showDogsLitters={INWIZARD_DOGS_LITTERS}
                    goToStep={goToStep}
                    onPublish={attemptPublish}
                    onSaveLater={handleSaveAndExit}
                    submitting={submitting}
                    isEdit={isEdit}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </section>

          {/* RIGHT LIVE PREVIEW (desktop only) — widoczny też na kroku Podsumowanie (układ „Birch Haus") */}
          <section className="hidden lg:block w-full lg:w-[420px] xl:w-[460px] flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <LivePreview data={form} />
            </div>
          </section>
        </div>
      </main>

      {/* MOBILE: wspólny WizardMobileBar (pływający podgląd + dolny pasek akcji + arkusz) */}
      <WizardMobileBar
        prefix="wizard-v2"
        step={step}
        totalSteps={STEPS.length}
        isLastStep={isLastStep}
        submitting={submitting}
        nextDisabled={submitting || (isLastStep && !termsAccepted && !isEdit)}
        publishLabel="Opublikuj"
        onBack={() => { if (step > 1) { haptics.tap(); setStep(step - 1); } }}
        onSaveExit={handleSaveAndExit}
        onNext={handleNext}
        previewNode={<LivePreview data={form} />}
      />
      </div>
    </StudioShell>
  );
}
